import {groupByUniqueKey} from '@benchling/js-utils';

import {trackEvent} from 'analytics/Analytics';
import {ANALYSIS_TOOL_ANALYTICS_ACTIONS, RERUN_SOURCE} from 'constants/AnalyticsConstants';
import * as PipelineDAO from 'pipelines/network/PipelineDAO';
import {PipelineGraphQL} from 'pipelines/PipelineTypes';
import {traversePipeline} from 'pipelines/StepLineageUtils';

type Pipeline = Pick<PipelineGraphQL, 'steps' | 'id'>;

/**
 * Re-run steps.
 *
 * This filters out steps that are descendants of each other, since descendant steps will naturally re-run
 * following the ancestor steps.
 *
 * This filtering is not strictly necessary for correctness, since the backend has handling to avoid
 * simultaneously re-running descendant steps; filtering here as well, however, keeps the
 * `TRIGGERED_STEP_RERUN` analytics metadata consistent.
 */
export async function rerunSteps(pipeline: Pipeline, ...stepIds: Array<string>): Promise<void> {
  const stepIdToStep = groupByUniqueKey(pipeline.steps, (step) => step.id);
  const downstreamSteps = traversePipeline(pipeline, {
    startingSteps: stepIds
      .flatMap((stepId) => stepIdToStep[stepId].outgoingEdges)
      .map((edge) => stepIdToStep[edge.toStep.id]),
  });
  const downstreamStepIds = new Set(downstreamSteps.map((step) => step.id));
  const filteredStepIds = stepIds.filter((stepId) => !downstreamStepIds.has(stepId));

  await PipelineDAO.addAndUpdatePipelineSteps(pipeline.id, {
    stepsToAdd: [],
    stepsToUpdate: filteredStepIds.map((stepId) => ({stepId, rerunAfterUpdate: true})),
  });

  filteredStepIds.forEach((stepId) => {
    trackEvent(ANALYSIS_TOOL_ANALYTICS_ACTIONS.TRIGGERED_STEP_RERUN, {
      rerun_source: RERUN_SOURCE.STEP,
      step_id: stepId,
      step_type: stepIdToStep[stepId].stepType,
      pipeline_id: pipeline.id,
    });
  });
}
